import {
  AuthenticationDetails,
  CognitoIdToken,
  CognitoUserPool,
  CognitoUser,
  CognitoUserSession,
  CognitoUserAttribute,
  CookieStorage,
  ISignUpResult,
  ICognitoStorage,
  CognitoAccessToken,
  CognitoRefreshToken,
  ClientMetadata
} from 'amazon-cognito-identity-js';

import {
  RememberedDevice,
  isPasswordLogin,
  isMfaLogin,
  PasswordLoginOptions,
  MfaLoginOptions
} from './common';
import { AuthData, ForgotPasswordResponse, SamlTokens } from '../types';
import {
  getConfig,
  getCookieOptions,
  getUrlEncodedFormBody,
  clearCookieStorage
} from '../utils';
import l10n from '@built/i18n';

const CONFIG = {
  region: getConfig('REACT_APP_REGION'),
  userPoolId: getConfig('REACT_APP_USER_POOL_ID'),
  clientId: getConfig('REACT_APP_CLIENT_ID'),
  userPoolDomainPrefix: getConfig('REACT_APP_USER_POOL_DOMAIN_PREFIX')
};

// AWS.config.region = CONFIG.region;

// Temp user used for multi-step authentication (e.g., MFA, initial password
// set)
let tempUser: CognitoUser;

/**
 * CognitoIdToken actually extends a CognitoJwtToken class from the SDK, but
 * the TS types don't expose the payload.
 */
interface CognitoJwtToken extends CognitoIdToken {
  payload: {
    'cognito:groups'?: string[];
    email?: string;
    [key: string]: undefined | string | string[];
  };
}

/**
 * Log in a user.
 *
 * This method has the following call modes:
 *   1. With a username + password
 *   2. With an MFA code + MFA type
 *
 * Typically the method will be called once with a username and password. If
 * the authentication system indicates that an MFA code is needed, this
 * method should be called again with the MFA code to complete the login.
 */
export function login(
  options: PasswordLoginOptions | MfaLoginOptions,
  useCustomAuth = true
) {
  return new Promise<AuthData>((resolve, reject) => {
    if (isPasswordLogin(options)) {
      const { username, password } = options;

      tempUser = getCognitoUser(username);

      const authDetails = new AuthenticationDetails({
        Username: username,
        Password: password
      });

      tempUser.authenticateUser(authDetails, {
        onSuccess(session) {
          getAuthData(tempUser, session).then((authData) => {
            resolve(authData as AuthData);
          });
        },

        onFailure(error) {
          reject(error);
        },

        newPasswordRequired(userAttributes) {
          resolve({
            email: userAttributes.email,
            emailVerified: userAttributes.email_verified,
            newPasswordRequired: true
          });
        },

        totpRequired(name: string) {
          resolve({ needsMfa: name });
        },

        mfaRequired(name: string, mfaParams: {}) {
          resolve({ needsMfa: name, mfaParams });
        },

        customChallenge(params) {
          reject(
            new Error(
              l10n.t(
                'cognito:customAuthChallengesAreNoLonger',
                'Custom auth challenges are no longer supported'
              )
            )
          );
        }
      });
    } else if (isMfaLogin(options)) {
      const { code, mfaType, rememberDevice } = options;
      tempUser.sendMFACode(
        code,
        {
          onFailure(error) {
            reject(error);
          },
          onSuccess(session) {
            getAuthData(tempUser, session).then((authData) => {
              setDeviceRemember(tempUser, rememberDevice)
                .then(() => {
                  resolve(authData as AuthData);
                })
                .catch((error) => {
                  reject(error);
                });
            });
          }
        },
        mfaType
      );
    }
  });
}

/**
 * Special handling for login during email verification flow
 */
export function loginEmailVerification(
  previousEmail: string,
  password: string
) {
  return new Promise<AuthData>((resolve, reject) => {
    const user = getCognitoUser(previousEmail);

    const authDetails = new AuthenticationDetails({
      Username: previousEmail,
      Password: password
    });

    user.authenticateUser(authDetails, {
      onSuccess(session) {
        getAuthData(user, session).then((authData) => {
          resolve(authData as AuthData);
        });
      },

      onFailure(error) {
        reject(error);
      }
    });
  });
}

/**
 * Set user preference for remembering the particular device the user is currently using
 *
 * @param user CognitoUser instance
 * @param remember if true, will inform cognito to remember the user's device; if false it
 *   will tell cognito not to remember
 */
export function setDeviceRemember(
  user: CognitoUser,
  remember: boolean | undefined
): Promise<void> {
  return new Promise((resolve, reject) => {
    const callbacks = {
      onSuccess(success: string) {
        resolve();
      },
      onFailure(error: Error) {
        reject(error);
      }
    };
    if (remember === true) {
      user.setDeviceStatusRemembered(callbacks);
    } else {
      user.setDeviceStatusNotRemembered(callbacks);
    }
  });
}

/**
 * Log out the currently logged in user.
 */
export function logout() {
  return getUser().then(({ user }) => {
    if (user) {
      user.signOut();
    }
    clearCookieStorage();
  });
}

/**
 * Set the initial password for a new user account.
 */
export function setInitialPassword(
  password: string,
  clientMetadata?: ClientMetadata
): Promise<AuthData> {
  return new Promise((resolve, reject) => {
    // This assumes 'user' is set to a partially authenticated user
    tempUser.completeNewPasswordChallenge(
      password,
      {},
      {
        onSuccess(result: CognitoUserSession) {
          resolve({
            username: tempUser.getUsername(),
            accessToken: result.getAccessToken().getJwtToken(),
            idToken: result.getIdToken().getJwtToken(),
            refreshToken: result.getRefreshToken().getToken()
          });
        },
        onFailure(error) {
          reject(error);
        }
      },
      clientMetadata
    );
  });
}

/**
 * Signs up a new user given the email and password. The registrationToken is used
 * for validation purposes.
 *
 * @param email
 * @param password
 * @param registrationToken
 */
export function userSignUp(
  email: string,
  password: string,
  registrationToken: string,
  clientMetadata?: ClientMetadata
) {
  return new Promise<ISignUpResult>((resolve, reject) => {
    // email is a required user attribute in the user pool
    let attributeList: CognitoUserAttribute[] = [];
    attributeList.push(
      new CognitoUserAttribute({
        Name: 'email',
        Value: email
      })
    );

    // for custom auth flows the mfaType must be set to 'init' to begin
    // the process
    attributeList.push(
      new CognitoUserAttribute({
        Name: 'custom:mfaType',
        Value: 'init'
      })
    );

    // registration token is not a writable user attribute, but it's
    // still required during the PreSignUp lambda to verify the user
    let validationList: CognitoUserAttribute[] = [];
    validationList.push(
      new CognitoUserAttribute({
        Name: 'registrationToken',
        Value: registrationToken
      })
    );

    // sign the user up; this will kick off a lambda to verify the provided
    // attribute/validation data. If that works, the lambda will auto confirm
    // the user and auto verify the email address
    const userPool = getCognitoUserPool();
    userPool.signUp(
      email,
      password,
      attributeList,
      validationList,
      function (error, result) {
        if (error) {
          reject(error);
          return;
        }
        resolve(result as ISignUpResult);
      },
      clientMetadata
    );
  });
}

/**
 * Initiate the password reset process for a user
 */
export function forgotPassword(email: string) {
  return new Promise<ForgotPasswordResponse>((resolve, reject) => {
    const user = getCognitoUser(email);
    user.forgotPassword({
      onSuccess(data) {
        resolve(data);
      },
      onFailure(error) {
        reject(error);
      },
      inputVerificationCode(data) {
        resolve(data);
      }
    });
  });
}

/**
 * Complete the password reset process for a user
 */
export function resetPassword(
  username: string,
  code: string,
  newPassword: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = getCognitoUser(username);
    user.confirmPassword(code, newPassword, {
      onSuccess() {
        resolve();
      },
      onFailure(error) {
        reject(error);
      }
    });
  });
}

/**
 * Return tokens for an authenticated user
 */
export function getAuthData(user?: CognitoUser, session?: CognitoUserSession) {
  const promise: Promise<{ user?: CognitoUser; session?: CognitoUserSession }> =
    !user || !session ? getUser() : Promise.resolve({ user, session });
  return promise.then((authData) => {
    const _user = authData.user;
    const _session = authData.session;
    if (_user && _session) {
      const idToken = <CognitoJwtToken>_session.getIdToken();

      return {
        username: _user.getUsername(),
        email: idToken.payload.email,
        accessToken: _session.getAccessToken().getJwtToken(),
        idToken: idToken.getJwtToken(),
        refreshToken: _session.getRefreshToken().getToken()
      };
    }
    return;
  });
}

/**
 * Get the currently active user, if one is active.
 */
export function getUser(clientId?: string): Promise<{
  user?: CognitoUser;
  session?: CognitoUserSession;
}> {
  return new Promise<{ user?: CognitoUser; session?: CognitoUserSession }>(
    (resolve, reject) => {
      const userPool = getCognitoUserPool(clientId);
      const user = <CognitoUser | null>userPool.getCurrentUser();
      if (!user) {
        resolve({ user: undefined, session: undefined });
      } else {
        user.getSession((error: Error | null, session: CognitoUserSession) => {
          if (error) {
            reject(error);
          } else {
            resolve({ user, session });
          }
        });
      }
    }
  );
}

/**
 * Get the currently logged in user. Throw if no user is authenticated.
 */
export function getAuthorizedUser(): Promise<{
  user: CognitoUser;
  session: CognitoUserSession;
}> {
  return getUser().then(({ user, session }) => {
    if (!user || !session) {
      throw new Error(
        l10n.t(
          'cognito:noUserIsCurrentlyLoggedIn',
          'No user is currently logged in'
        )
      );
    }
    return { user, session };
  });
}

/**
 * Retrieve a validation code that can be used to register a TOTP
 * authenticator.
 */
export function getTokenSecret() {
  return getAuthorizedUser().then(
    ({ user }) =>
      new Promise<string>((resolve, reject) => {
        user.associateSoftwareToken({
          onFailure(error) {
            reject(error);
          },
          associateSecretCode(code: string) {
            resolve(code);
          }
        });
      })
  );
}

/**
 * @desc Given an oauth2 authorization code from Cognito, we
 * use the current user pool's hosted oauth2 endpoints
 * for exchanging the code for JWT auth tokens. This
 * will also take care off establishing a cognito
 * "session" that handles setting session storage
 * for a redirect.
 *
 * @param clientId the client ID responsible for the SSO
 * @param authCode the auth code to exchange for JWT tokens
 * @param redirectUrl the predetermined redirect URL used during
 * the SSO flow
 */
export async function exchangeAuthCode(
  clientId: string,
  authCode: string,
  redirectUrl: string
): Promise<AuthData | undefined> {
  const url = `https://${CONFIG.userPoolDomainPrefix}.auth.${CONFIG.region}.amazoncognito.com/oauth2/token`;
  const headers = {
    accept: 'application/json',
    'content-type': 'application/x-www-form-urlencoded'
  };
  const body = getUrlEncodedFormBody({
    grant_type: 'authorization_code',
    client_id: clientId,
    code: authCode,
    redirect_uri: redirectUrl
  });

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body
  });

  if (response.status !== 200) {
    throw new Error(
      l10n.t('cognito:oAuthTokenExchangeFailed', 'OAuth token exchange failed.')
    );
  }

  const tokens = (await response.json()) as SamlTokens;
  createSessionFromSamlTokenResponse(tokens);
  return {
    accessToken: tokens.access_token,
    idToken: tokens.id_token,
    refreshToken: tokens.refresh_token,
    ssoClientId: clientId
  };
}

function createSessionFromSamlTokenResponse(tokens: SamlTokens) {
  // create and decode the id token to retrieve the email address
  // and client ID used for auth
  const idToken = new CognitoIdToken({
    IdToken: tokens.id_token
  });

  // create a new cognito user for the email address and create
  // the user session which will create the cookies we need
  // for a redirect
  const payload = idToken.decodePayload();
  const user = getCognitoUser(payload.sub, payload.aud);
  user.setSignInUserSession(
    new CognitoUserSession({
      AccessToken: new CognitoAccessToken({ AccessToken: tokens.access_token }),
      IdToken: idToken,
      RefreshToken: new CognitoRefreshToken({
        RefreshToken: tokens.refresh_token
      })
    })
  );
}

/**
 * Complete the registration process for a TOTP authenticator.
 */
export function verifySoftwareToken(
  code: string,
  authenticatorName = 'authenticator'
) {
  return getAuthorizedUser().then(
    ({ user }) =>
      new Promise<CognitoUser>((resolve, reject) => {
        user.verifySoftwareToken(code, authenticatorName, {
          onSuccess: () => {
            resolve(user);
          },
          onFailure: (error) => {
            reject(error);
          }
        });
      })
  );
}

export function setSmsPhoneNumber(phoneNumber: string) {
  return setUserAttributes({ phone_number: phoneNumber });
}

export function resendSmsCode(): Promise<boolean> {
  return getAuthorizedUser().then(({ user }) => {
    return new Promise<boolean>((resolve, reject) => {
      user.getAttributeVerificationCode('phone_number', {
        onSuccess() {
          resolve(true);
        },

        onFailure(error) {
          reject(error);
        },

        inputVerificationCode() {
          resolve(true);
        }
      });
    });
  });
}

export function verifySmsPhoneNumber(code: string) {
  return verifyUserAttribute('phone_number', code);
}

/**
 * Enable MFA for a user with a verified software token or SMS phone number
 */
export function enableMfa(type: 'sms' | 'totp') {
  return getAuthorizedUser().then(({ user }) =>
    new Promise<CognitoUser>((resolve, reject) => {
      if (type === 'sms' || type === 'totp') {
        const data = { PreferredMfa: true, Enabled: true };

        user.setUserMfaPreference(
          type === 'sms' ? data : null,
          type === 'totp' ? data : null,
          (error) => {
            if (error) {
              reject(error);
            } else {
              resolve(user);
            }
          }
        );
      } else {
        // TODO - Security question MFA should be handled using a custom auth
        // flow and the attributes configured below, but we may need to add
        // logic to disable sms and totp MFA. Passing null for both in
        // setUserMfaPreference results in an error
        resolve(user);
      }
    })
      .then(() => {
        return setUserAttributes({
          'custom:mfaType': type
        });
      })
      .then(() => user)
  );
}

/**
 * Disable MFA on a user account
 */
export function disableMfa(): Promise<void> {
  return getAuthorizedUser().then(({ user, session }) =>
    getUserMfaSettings(user, session).then(
      (mfaSettings) =>
        new Promise((resolve, reject) => {
          const callback = (
            error: Error | undefined,
            result: string | undefined
          ) => {
            if (error) {
              reject(error);
            } else {
              resolve();
            }
          };

          const newSettings = { PreferredMfa: false, Enabled: false };

          if (mfaSettings[0] === 'SOFTWARE_TOKEN_MFA') {
            user.setUserMfaPreference(null, newSettings, callback);
          } else {
            user.setUserMfaPreference(newSettings, null, callback);
          }
        })
    )
  );
}

/**
 * Return a list of remembered devices
 */
export function getRememberedDevices(): Promise<RememberedDevice[]> {
  return getAuthorizedUser().then(
    ({ user }) =>
      new Promise<RememberedDevice[]>((resolve, reject) => {
        user.listDevices(10, null, {
          onSuccess(result) {
            resolve(
              result.Devices.map((device) => ({
                created: device.DeviceCreateDate,
                id: device.DeviceKey,
                name: device.DeviceAttributes.find(
                  (attr) => attr.Name === 'device_name'
                )!.Value,
                lastAuthenticated: device.DeviceLastAuthenticatedDate,
                lastModified: device.DeviceLastModifiedDate,
                attributes: device.DeviceAttributes.reduce(
                  (allAttrs, attr) => ({
                    ...allAttrs,
                    [attr.Name]: attr.Value
                  }),
                  {} as { [attribute: string]: string }
                )
              }))
            );
          },
          onFailure(error) {
            reject(error);
          }
        });
      })
  );
}

/**
 * Forget the specified device, or all devices if no ID is specified
 */
export function forgetDevice(deviceId?: string): Promise<boolean> {
  return getAuthorizedUser().then(
    ({ user }) =>
      new Promise<boolean>((resolve, reject) => {
        const callback = {
          onSuccess() {
            resolve(true);
          },
          onFailure(error: Error) {
            reject(error);
          }
        };
        if (deviceId) {
          user.forgetSpecificDevice(deviceId, callback);
        } else {
          user.forgetDevice(callback);
        }
      })
  );
}

/**
 * Get the attributes for the authenticated user
 */
export function getUserData() {
  return getAuthorizedUser().then(({ user, session }) =>
    new Promise<{
      [key: string]: string | number | boolean;
    }>((resolve, reject) => {
      user.getUserAttributes((error, result) => {
        if (error) {
          reject(error);
        } else {
          const attrs = result
            ? result.reduce(
                (allAttrs, attr) => ({
                  ...allAttrs,
                  [attr.getName()]: attr.getValue()
                }),
                {}
              )
            : {};
          resolve(attrs);
        }
      });
    }).then((attributes) =>
      getUserMfaSettings(user, session).then((mfaSettings) => ({
        attributes,
        mfa: mfaSettings
      }))
    )
  );
}

/**
 * Set attributes on the authenticated user
 */
export function setUserAttributes(attrs: {
  [name: string]: string;
}): Promise<void> {
  return getAuthorizedUser().then(({ user }) => {
    return new Promise((resolve, reject) => {
      const cognitoAttrs = Object.keys(attrs).map((name) => ({
        Name: name,
        Value: attrs[name]
      }));
      user.updateAttributes(cognitoAttrs, function (error, result) {
        if (error) {
          reject(error);
        } else {
          resolve();
        }
      });
    });
  });
}

/**
 * Verify an attribute on the authenticated user
 */
export function verifyUserAttribute(name: string, code: string): Promise<void> {
  return getAuthorizedUser().then(({ user }) => {
    return new Promise((resolve, reject) => {
      user.verifyAttribute(name, code, {
        onSuccess() {
          resolve();
        },
        onFailure(error) {
          reject(error);
        }
      });
    });
  });
}

/**
 * Delete an attribute on the authenticated user
 */
export function deleteUserAttribute(name: string): Promise<void> {
  return getAuthorizedUser().then(
    ({ user }) =>
      new Promise((resolve, reject) => {
        user.deleteAttributes([name], (error) => {
          if (error) {
            reject(error);
          } else {
            resolve();
          }
        });
      })
  );
}

function getCognitoUser(username: string, clientId?: string): CognitoUser {
  return <CognitoUser>new CognitoUser({
    Username: username,
    Pool: getCognitoUserPool(clientId),
    Storage: getCognitoStorage()
  });
}

function getCognitoStorage(): ICognitoStorage {
  const cookieOptions = getCookieOptions();

  return new CookieStorage({
    domain: cookieOptions.domain!,
    // force cookies to expire when the browser closes
    expires: undefined,
    path: cookieOptions.path!,
    secure: cookieOptions.secure!,
    sameSite: 'none'
  });
}

function getCognitoUserPool(clientId?: string): CognitoUserPool {
  return new CognitoUserPool({
    UserPoolId: CONFIG.userPoolId,
    ClientId: clientId || CONFIG.clientId,
    Storage: getCognitoStorage()
  });
}

/**
 * Get MFA settings from a user's Cognito profile.
 */
function getUserMfaSettings(user: CognitoUser, session: CognitoUserSession) {
  return new Promise<string[]>((resolve, reject) => {
    user.getUserData(function (error, userData) {
      if (error) {
        reject(error);
      } else {
        if (userData && userData.UserMFASettingList) {
          resolve(userData.UserMFASettingList);
        } else {
          resolve([]);
        }
      }
    });
  });
}
